import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import pl from "./locales/pl.json";

/**
 * i18next initialisation for the landing SPA.
 *
 * Mirrors the per-app i18n pattern from ShadowVerge — each SPA owns
 * its own resources, localStorage key uses the `us:` namespace prefix
 * (consistent with `us:theme`), and Polish browsers get a Polish
 * first-load when there's no saved preference.
 */

export type Language = "en" | "pl";
export const SUPPORTED: Language[] = ["en", "pl"];

const STORAGE_KEY = "us:language";

function pickInitial(): Language {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "pl") return saved;
  } catch {
    /* localStorage may be blocked */
  }
  const nav = (typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : "en"
  ).toLowerCase();
  if (nav.startsWith("pl")) return "pl";
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pl: { translation: pl },
  },
  lng: pickInitial(),
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnNull: false,
});

i18n.on("languageChanged", (lng) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
    // Mirror the user's pick on <html lang="..."> so browsers + Lighthouse
    // see it for accessibility and SEO.
    document.documentElement.lang = lng;
  } catch {
    /* ignore */
  }
});

// Set initial <html lang> at boot.
if (typeof document !== "undefined") {
  document.documentElement.lang = i18n.language || "en";
}

export default i18n;
